import { Injectable } from "@angular/core";
import { Slides } from "@interfaces/slides";
import { safeArrayAccess, safeArraySlice } from "@core/safe-access.utility";

/**
 * Encapsulates the store page swiper/banner autoplay management and the store
 * slider data formatting that previously lived directly on `StorePage`.
 *
 * Each method receives the host `StorePage` instance so that behaviour and the
 * exact state mutations remain identical to the pre-refactor implementation.
 */
@Injectable()
export class StoreSwiperManagerService {
  setSwiperInstance(host: any, swiper: any): void {
    // Guard: Return early if component is not active or swiper is invalid
    if (!host.isComponentActive || !swiper) {
      return;
    }

    host.swiperInstance = swiper;

    // Clear any existing intervals to prevent memory leaks
    if (host.interval) {
      clearInterval(host.interval);
      host.interval = null;
    }

    // Verify swiper is fully initialized before setting up interval
    if (!swiper?.initialized) {
      host.authService.errorHandlerLogs("Swiper instance not fully initialized yet.");
      return;
    }

    // Start a new interval after a delay to check and control autoplay
    setTimeout(() => {
      // Double-check component is still active before creating interval
      if (!host.isComponentActive) {
        return;
      }

      host.interval = setInterval(() => {
        // Guard: Check if component is still active and swiper instance exists
        if (!host.isComponentActive || !host.swiperInstance || !host.swiperInstance.autoplay) {
          // Clear interval if conditions are not met
          if (host.interval) {
            clearInterval(host.interval);
            host.interval = null;
          }
          return;
        }

        try {
          if (!host.autoSlide) {
            // If autoSlide is false, stop autoplay
            host.swiperInstance?.autoplay?.stop();
          } else {
            // If autoSlide is true, start or resume autoplay
            // Use autoplay's built-in mechanism instead of manual slideNext to prevent recursion
            if (host.swiperInstance?.autoplay && !host.swiperInstance.autoplay.running) {
              host.swiperInstance.autoplay.start();
            }
          }
        } catch (error) {
          host.authService.errorHandlerLogs("Error in swiper autoplay control:", error);
          // Clear interval on error to prevent further issues
          if (host.interval) {
            clearInterval(host.interval);
            host.interval = null;
          }
        }
      }, 4500);
    }, 4500);
  }

  setSwiperInstanceJackpot(host: any, swiper: any): void {
    // Guard: Return early if component is not active or swiper is invalid
    if (!host.isComponentActive || !swiper) {
      return;
    }

    // Verify swiper is fully initialized before proceeding
    if (!swiper?.initialized) {
      host.authService.errorHandlerLogs("Jackpot swiper instance not fully initialized yet.");
      return;
    }

    // Use only the Angular Swiper component instance - DO NOT create manual instance
    host.slides = swiper;

    // Clear any existing intervals to prevent memory leaks
    if (host.interval) {
      clearInterval(host.interval);
      host.interval = null;
    }

    // Start a new interval to check and control autoplay
    host.interval = setInterval(() => {
      // Guard: Check if component is still active and slides instance exists
      if (!host.isComponentActive || !host.slides) {
        // Clear interval if conditions are not met
        if (host.interval) {
          clearInterval(host.interval);
          host.interval = null;
        }
        return;
      }

      // Additional guard: Check if swiper element still exists in DOM
      if (host.slides.el && !document.contains(host.slides.el)) {
        host.authService.errorHandlerLogs("Swiper element no longer in DOM, clearing interval");
        if (host.interval) {
          clearInterval(host.interval);
          host.interval = null;
        }
        return;
      }

      try {
        // Verify slides instance is still valid before accessing properties
        if (!host.slides || host.slides.destroyed) {
          if (host.interval) {
            clearInterval(host.interval);
            host.interval = null;
          }
          return;
        }

        if (!host.autoplayJackpot) {
          // If autoplayJackpot is false, stop autoplay and go to the first slide
          if (host.slides.autoplay && typeof host.slides.autoplay.stop === "function") {
            host.slides.autoplay.stop();
          }
          if (typeof host.slides?.slideTo === "function") {
            host.slides?.slideTo(0);
          }
        } else {
          // If autoplayJackpot is true, use autoplay's built-in mechanism to prevent recursion
          if (host.slides.autoplay && typeof host.slides.autoplay.start === "function") {
            if (!host.slides.autoplay.running) {
              host.slides.autoplay.start();
            }
          }
        }
      } catch (error) {
        host.authService.errorHandlerLogs("Error in jackpot swiper autoplay control:", error);
        // Clear interval on error to prevent further issues
        if (host.interval) {
          clearInterval(host.interval);
          host.interval = null;
        }
      }
    }, 4500); // Adjusted interval time to match autoplay delay
  }

  // This method is used to update the store slider
  updateStoreSlider(host: any): void {
    host.storeSliderSubscription = host.storeService.slides$?.subscribe((storePromotion: Slides) => {
      if (!storePromotion?.slides || !Array.isArray(storePromotion.slides)) return;

      const slidesElement = storePromotion.slides;

      slidesElement.forEach((eachElement) => {
        const slideInfo = eachElement.slide_info;
        const content = eachElement.slider?.content?.[host.languageCodeSignal()];

        if (slideInfo?.style === "custom") {
          host.hasCustomStyle = true;
        } else if (slideInfo?.style === "bundle") {
          host.hasBundleStyle = true;
        }

        host.dailyPromoSliderData = slidesElement[0]?.slide_info;
        host.authService.currencyNodeDvData = slideInfo;

        if (slideInfo?.jackpot_value) {
          const rawValue = slideInfo.jackpot_value[host.preferredCurrency] ?? slideInfo.jackpot_value;
          const formattedJackpotValue = host.formatValue(rawValue);

          if (host.preferredCurrency && slideInfo.jackpot_value[host.preferredCurrency] !== undefined) {
            slideInfo.jackpot_value[host.preferredCurrency] = formattedJackpotValue;
          } else {
            slideInfo.jackpot_value = formattedJackpotValue;
          }
        }

        // Fire-and-forget: the returned Promise is intentionally not awaited.
        // On failure we log and leave content.discount_description unchanged (non-fatal).
        host.authService
          .getDVFromString(content?.discount_description)
          .then((dvString: string) => {
            if (dvString) {
              content.discount_description = host.formatNumbersInString(dvString);
            }
          })
          .catch((err) => {
            this.logDvError(host, 'discount', err);
          });

        // Fire-and-forget: the returned Promise is intentionally not awaited.
        // On failure we log and leave content.cta_description unchanged (non-fatal).
        host.authService
          .getDVFromString(content?.cta_description)
          .then((dvString: string) => {
            if (dvString) {
              content.cta_description = host.formatNumbersInString(dvString);
            }
          })
          .catch((err) => {
            this.logDvError(host, 'cta', err);
          });
      });

      const firstSlide = safeArrayAccess(storePromotion?.slides, 0, null);
      const isSpecialPromotion = firstSlide?.slide_info?.slide_type === "special";

      let dailyPromotionIndex = 0;

      if (isSpecialPromotion && firstSlide) {
        host.firstRetoolSlideContentSignal.set(firstSlide);
        host.firstRetoolSlideContent = host.firstRetoolSlideContentSignal();
        host.visibilityControl.set(host.firstRetoolSlideContent?.visibility_control);
        host.operatorSelection.set(host.firstRetoolSlideContent?.operator_selection);

        host.isNotExpiredCard = firstSlide.slide_info?.expiry_date
          ? new Date(firstSlide.slide_info.expiry_date.seconds * 1000).getTime() > Date.now()
          : false;

        host.autoSlide = firstSlide.slider?.auto_slide;
        dailyPromotionIndex = 1;
      }

      const bundlePromotionIndex = isSpecialPromotion ? 2 : 1;
      const dailyPromotionSlide = safeArrayAccess(storePromotion?.slides, dailyPromotionIndex, null);
      host.dailyPromotionSlideDataSignal.set(dailyPromotionSlide);
      host.dailyPromotionSlideData = host.dailyPromotionSlideDataSignal();
      host.bundlePromotionSlideData = safeArraySlice(storePromotion?.slides, bundlePromotionIndex);

      this.applyVisibilityControl(host);
    });
  }

  // Apply visibility logic based on operators and selected events
  applyVisibilityControl(host: any): void {
    const visibility = host.visibilityControl();
    const operator = host.operatorSelection();

    if (visibility === "show") {
      host.isBannerDisplay.set(operator === "AND" ? this.checkBothActionsActive(host) : this.checkAnyActionActive(host));
    } else if (visibility === "hide") {
      host.isBannerDisplay.set(operator === "AND" ? !this.checkBothActionsActive(host) : !this.checkAnyActionActive(host));
    }
  }

  /** Helper functions to check event triggers */
  checkBothActionsActive(host: any): boolean {
    // Implement logic to check if both actions are active
    return host.jackpotTriggerActiveCount() && host.isCreditRefresh();
  }

  checkAnyActionActive(host: any): boolean {
    // Check if jackpotTriggerActiveCount is greater than 0
    return host.jackpotTriggerActiveCount() > 0 || host.isCreditRefresh();
  }

  prev(host: any) {
    try {
      host.slides?.slidePrev(500);
    } catch (e) {
      return e;
    }
  }

  /**
   * Safely logs a getDVFromString failure without risking a secondary unhandled rejection.
   * errorHandlerLogs is deferred into .then() so any synchronous throw is caught by
   * the subsequent .catch() rather than escaping to the caller.
   */
  private logDvError(host: any, label: string, err: unknown): void {
    void Promise.resolve()
      .then(() => host?.authService?.errorHandlerLogs?.(`Error from getDVFromString (${label}):`, err))
      .catch((logErr) => {
        try { console.error(`getDVFromString (${label}) logging failed:`, logErr, 'original error:', err); } catch { /* ignore */ }
      });
  }

  next(host: any) {
    try {
      host.slides?.slideNext(500);
    } catch (e) {
      return e;
    }
  }

  slidePrev(host: any) {
    try {
      host.storeCardSlider?.swiperRef?.slidePrev(500);
    } catch (e) {
      return e;
    }
  }

  slideNext(host: any) {
    try {
      host.storeCardSlider?.swiperRef?.slideNext(500);
    } catch (e) {
      return e;
    }
  }

  // OnSlideChanges
  onSlideChange(host: any): void {
    const index = host.slider?.swiperRef?.activeIndex;
    host.slides?.slideTo(index);
  }
}
